/**
 * constants/bannerWidget.ts
 * Static configuration for banner bar widgets.
 *
 * Provides the canonical registry of widget types used by the banner bar,
 * layout dropdown widget toggles, and banner rendering logic.
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (only `import type` from `types/`)
 * - No imports from `utils/` or other domain constants
 * - No side effects, pure data only
 * - Immutable maps use `as const satisfies` or explicit `Readonly<Record<>>` annotations
 *
 * @see types/shared/BannerWidget.ts — type definitions
 * @see types/shared/Layout.ts — BannerConfig persistence schema
 */

import { Clock, Target, Calendar } from 'lucide-react';

import type { BannerWidgetId, BannerWidgetConfig } from '@/types/shared/BannerWidget';

// ===== WIDGET IDENTIFIERS =====

/**
 * All banner widget IDs.
 * Ordered by default display position (left to right).
 */
export const BANNER_WIDGET_IDS = [
  'clock',
  'daily-objective',
  'economic-events',
] as const satisfies readonly BannerWidgetId[];

// ===== WIDGET REGISTRY =====

/**
 * Static configuration for banner widgets.
 * Rendering component deferred to Phase 3 (BannerBar build).
 */
export const BANNER_WIDGETS: readonly BannerWidgetConfig[] = [
  {
    id: 'clock',
    label: 'Clock',
    description: 'Time-of-day clock in your display timezone',
    Icon: Clock,
    defaultEnabled: true,
  },
  {
    id: 'daily-objective',
    label: 'Daily Objective',
    description: 'Your primary trading objective for today',
    Icon: Target,
    defaultEnabled: true,
  },
  {
    id: 'economic-events',
    label: 'Economic Events',
    description: 'Upcoming economic calendar releases',
    Icon: Calendar,
    defaultEnabled: true,
  },
] as const;

// ===== DISPLAY MAP =====

/**
 * Human-readable labels for banner widgets.
 * Explicit literal map with `satisfies` for compile-time exhaustiveness.
 */
export const BANNER_WIDGET_DISPLAY_MAP = {
  'clock': 'Clock',
  'daily-objective': 'Daily Objective',
  'economic-events': 'Economic Events',
} as const satisfies Readonly<Record<BannerWidgetId, string>>;

// ===== COMPILE-TIME COMPLETENESS CHECKS =====

type _AllWidgetIdsCovered = Exclude<BannerWidgetId, (typeof BANNER_WIDGET_IDS)[number]> extends never ? true : never;

/**
 * @internal
 * Compile-time coverage check — bundled export to eliminate unused variable warnings
 * while maintaining drift protection. Do NOT re-export from barrels.
 */
export type _BannerWidgetTypeChecks = [_AllWidgetIdsCovered];
